import S from 'common/service/sanctuary';
import React from 'react';
import { connect } from 'react-redux';
import AdvancedSearch, { DisplayCriteria } from 'common/components/search/advanced';
import { defineMessages, FormattedMessage } from 'react-intl';
import { STATUS_LOGIN, STATUS_LOGOUT, STATUS_ONLINE, STATUS_OFFLINE, STATUS_CONNECTED, STATUS_DISCONNECTED, STATUS_UNKNOWN, messages as statusMessages } from 'common/components/search/status';
import { types } from 'common/store/search/advanced/criteria';

import {
  selectors,
  actions,
} from '@sma/store/device/search';

import {
  PLAIN,
} from 'common/store/search/advanced/criteria/types';

export const METER_ID = 'deviceId';
export const VENDOR = 'vendor';
export const TAG_ID = 'tagId';
export const NODE_ID = 'nodeId';
export const FIRMVARE_VERSION = 'firmwareVersion';
export const HARDWARE_VERSION = 'hardwareVersion';
export const DRIVER = 'driver';
export const GATEWAY = 'gateway';
export const LATEST_DATE_TIME = 'latestTime';
export const CREATED_DATE_TIME = 'createTime';
export const STATUS = 'updateTime';
export const CONSUMER_STATUS = 'consumerStatus';
export const NETWORK_STATUS = 'networkStatus';

export const messages = defineMessages({
  meterId: {
    id: 'meter.search.advanced.meterId',
    defaultMessage: 'Meter ID',
  },
  vendor: {
    id: 'meter.search.advanced.vendor',
    defaultMessage: 'Vendor',
  },
  tags: {
    id: 'meter.search.advanced.tags',
    defaultMessage: 'Tags',
  },
  nodeId: {
    id: 'meter.search.advanced.nodeId',
    defaultMessage: 'Node ID',
  },
  firmwareVersion: {
    id: 'meter.search.advanced.firmwareVersion',
    defaultMessage: 'Firmware',
  },
  hardwareVersion: {
    id: 'meter.search.advanced.hardwareVersion',
    defaultMessage: 'Hardware',
  },
  driver: {
    id: 'meter.search.advanced.driver',
    defaultMessage: 'Driver',
  },
  gateway : {
    id: 'meter.search.advanced.gateway',
    defaultMessage: 'Gateway',
  },
  createdDateTime: {
    id: 'meter.search.advanced.createdDateTime',
    defaultMessage: 'Created Date',
  },
  latestDateTime: {
    id: 'meter.search.advanced.latestDateTime',
    defaultMessage: 'Latest Date Time',
  },
  status : {
    id: 'meter.search.advanced.status',
    defaultMessage: 'Status',
  },
  consumerStatus : {
    id: 'meter.search.advanced.consumerStatus',
    defaultMessage: 'Consumer Status',
  },
  networkStatus : {
    id: 'meter.search.advanced.networkStatus',
    defaultMessage: 'Network Status',
  },
});

export const meterSearchCriteria = {
  [VENDOR]: {
    property: VENDOR,
    nullable: true,
    label: <FormattedMessage {...messages.vendor} />,
    type: PLAIN,
  },
  [TAG_ID]: {
    property: TAG_ID,
    nullable: true,
    label: <FormattedMessage {...messages.tags} />,
    type: PLAIN,
  },
  [FIRMVARE_VERSION]: {
    property: FIRMVARE_VERSION,
    nullable: false,
    label: <FormattedMessage {...messages.firmwareVersion} />,
    type: PLAIN,
  },
  [HARDWARE_VERSION]: {
    property: HARDWARE_VERSION,
    nullable: false,
    label: <FormattedMessage {...messages.hardwareVersion} />,
    type: PLAIN,
  },
   [GATEWAY]: {
    property: GATEWAY,
    nullable: false,
    equalsOnly: true,
    singleConstraint: true,
    label: <FormattedMessage {...messages.gateway} />,
    type: PLAIN,
  },
  [CREATED_DATE_TIME]: {
    property: CREATED_DATE_TIME,
    nullable: true,
    label: <FormattedMessage {...messages.createdDateTime} />,
    type: types.DATETIME,
  },
  [LATEST_DATE_TIME]: {
    property: LATEST_DATE_TIME,
    nullable: true,
    label: <FormattedMessage {...messages.latestDateTime} />,
    type: types.DATETIME,
  },
  [STATUS]: {
    property: STATUS,
    nullable: false,
    label: <FormattedMessage {...messages.status} />,
    type: types.CHOICE,
    singleConstraint: true,
    values: [
      {
        label: <FormattedMessage {...statusMessages[STATUS_ONLINE]} />,
        value: STATUS_ONLINE,
      },
      {
        label: <FormattedMessage {...statusMessages[STATUS_OFFLINE]} />,
        value: STATUS_OFFLINE,
      }],
  },
  [CONSUMER_STATUS]: {
    property: CONSUMER_STATUS,
    nullable: false,
    label: <FormattedMessage {...messages.consumerStatus} />,
    type: types.CHOICE,
    singleConstraint: true,
    values: [
      {
        label: <FormattedMessage {...statusMessages[STATUS_CONNECTED]} />,
        value: STATUS_CONNECTED,
      },
      {
        label: <FormattedMessage {...statusMessages[STATUS_DISCONNECTED]} />,
        value: STATUS_DISCONNECTED,
      },
      {
        label: <FormattedMessage {...statusMessages[STATUS_UNKNOWN]} />,
        value: STATUS_UNKNOWN,
      }],
  },
  [NETWORK_STATUS]: {
    property: NETWORK_STATUS,
    nullable: false,
    label: <FormattedMessage {...messages.networkStatus} />,
    type: types.CHOICE,
    singleConstraint: true,
    values: [
      {
        label: <FormattedMessage {...statusMessages[STATUS_LOGIN]} />,
        value: STATUS_LOGIN,
      },
      {
        label: <FormattedMessage {...statusMessages[STATUS_LOGOUT]} />,
        value: STATUS_LOGOUT,
      }],
  },
};

const MeterAdvancedSearch = ({
  showTagFilter,
  removeDevicesFilterByTag,
  tagValue,
  ...props
}) => (
  <AdvancedSearch
    className="meter-advanced-search"
    criteriaConfig={S.values(meterSearchCriteria)}
    showTagFilter={showTagFilter}
    removeDevicesFilterByTag={removeDevicesFilterByTag}
    tagValue={tagValue}
    {...props}
  />
);

export const DisplaySearch = ({
  criteria,
}) => {
  const fullCriteria = S.map(
    criterion => ({
      ...meterSearchCriteria[S.prop('property', criterion)],
      constraints: criterion.constraints,
    }),
    criteria,
  );

  return S.map(
    criterion => (
      <DisplayCriteria
        label={S.maybeToNullable(S.map(
          S.prop('label'),
          S.find(S.compose(S.equals(criterion.property), S.prop('property')), meterSearchCriteria),
        ))}
        criteria={criterion}
      />
    ),
    fullCriteria,
  );
};

export default connect(
  state => ({
    hasCriteria: selectors.hasCriteria(state),
    getCriteriaState: selectors.getCriteriaState(state),
    hasConstraints: selectors.hasConstraints(state),
    tagValue: state.device.devices.filterTag,
  }),
  actions,
)(MeterAdvancedSearch);
